import React from 'react';
import _ from 'lodash';
import { Button, Box, Stack, Typography, TableContainer, Paper, Table, TableHead, TableRow, TableBody, IconButton } from '@mui/material';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useMutation } from 'react-fetching-library';
import AddIcon from '@mui/icons-material/Add';
import { DebounceInput } from 'react-debounce-input';
import { styled, useTheme } from '@mui/material/styles';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { SmallFormTextField } from '../../../../../components/small-fields/SmallFormTextField';
import { AlertDialogContainer } from '../../../../../components/others/AlertDialogContainer';
import { submitCalendarDaySlotsAction } from '../../../../../services/request';
import { useDidMountEffect } from '../../../../../services/hooks';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0
  }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.grey[800],
    color: theme.palette.common.white
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14
  }
}));


export const SlotsControlForm = props => {
  const { data, submitCallback, handleClose, allActivities, selectedResort } = props;
  const theme = useTheme();
  const { loading, mutate } = useMutation(submitCalendarDaySlotsAction);
  const [showAlert, setShowAlert] = React.useState(false);
  const [severity, setSeverity] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [zsSlotsIdsEdited, setZsSlotsIdsEdited] = React.useState([]);

  const validationSchema = Yup.object({
    total_slots: Yup.string()
  });

  const initialValues = {
    total_slots: !_.isNil(data) ? data.total_slots : 0,
    zs_slots: !_.isNil(data) ? data.zs_slots : {}
  };

  const handleSubmit = async values => {
    values.edited_zs_slots = values.zs_slots.filter(slot => zsSlotsIdsEdited.includes(slot.id));
    const { error, payload } = await mutate(values, data.id);
    if (!error) {
      submitCallback(payload);
      setSeverity('success');
      setTitle('Zaktualizowano kontrolę miejsc');
      setMessage('');
      setShowAlert(true);
    } else {
      setSeverity('error');
      setTitle('Nie udało się zaktualizować kontroli miejsc');
      setMessage('');
      setShowAlert(true);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: values => {
      handleSubmit(values);
    }
  });

  const updateRowCallback = newRow => {
    const clonedObject = _.cloneDeep(formik.values.zs_slots);
    const idx = _.findIndex(clonedObject, obj => obj.id == newRow.id);
    const loadRateToInt = parseInt(newRow.maximum_load_rate);
    if (isNaN(loadRateToInt)) {
      clonedObject[idx].maximum_load_rate = 0;
    } else {
      clonedObject[idx].maximum_load_rate = loadRateToInt;
    }
    if (!zsSlotsIdsEdited.includes(clonedObject[idx].id)) {
      setZsSlotsIdsEdited([...zsSlotsIdsEdited, clonedObject[idx].id]);
    }

    formik.setFieldValue('zs_slots', clonedObject);
    setSortedData(clonedObject);
  };

  const [sortedData, setSortedData] = React.useState(formik.values.zs_slots);
  const [sortConfig, setSortConfig] = React.useState({ key: '', direction: 'asc' });

  const handleSort = (key, type) => {
    console.log(key, type);
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    const sortedArray = [...sortedData].sort((a, b) => {
      if (type === 'string') {
        if (direction === 'asc') {
          return a[key].localeCompare(b[key]);
        }

        return b[key].localeCompare(a[key]);

      } else if (type === 'number') {
        if (direction === 'asc') {
          return a[key] - b[key];
        }

        return b[key] - a[key];

      }
    });

    setSortedData(sortedArray);
    formik.setFieldValue('zs_slots', sortedArray);
    setSortConfig({ key, direction });
  };

  return (
    <>
      {showAlert && (
        <AlertDialogContainer
          severity={severity}
          title={title}
          message={message}
          setShowAlert={setShowAlert}
        />
      )}
      <form onSubmit={formik.handleSubmit}>
        <Box display="flex" flexDirection="column" mt={1} p={1} ml="5%" mr="5%">
          <Box sx={{ textAlign: 'center' }}>
            <Button
              color="primary"
              variant="contained"
              type="submit"
              sx={{ mb: 3 }}
              disabled={loading}
            >
          Zapisz
            </Button>
          </Box>
          <Stack flexDirection="row" mb={1} justifyContent="space-between">
            <Stack direction="column" alignItems="center" justifyContent="center" spacing={1} mb={2}>
              <Typography variant="subtitle2" flex="1 1 10%">Liczba zadeklarowanych miejsc w ośrodku</Typography>
              <SmallFormTextField
                sx= {{ width: '90%', height: '50%' }}
                name="total_slots"
                value={formik.values.total_slots}
                onChange={formik.handleChange}

                variant="outlined"
              />
            </Stack>
            <Stack direction="column" alignItems="center" justifyContent="center" spacing={1} mb={2}>
              <Typography variant="subtitle2" flex="1 1 10%">Liczba wolnych miejsc w ośrodku</Typography>
              <Typography color={
                data.resort_slots.free_slots <= 0 ? 'error'
                  : data.resort_slots.free_slots > 0 && data.resort_slots.free_slots < 40 ? '#B78103'
                    : 'green'}>{data.resort_slots.free_slots}</Typography>
            </Stack>
            <Stack direction="column" alignItems="center" justifyContent="center" spacing={1} mb={2}>
              <Typography variant="subtitle2" flex="1 1 10%">Liczba zajętych miejsc w ośrodku</Typography>
              <Typography>{data.resort_slots.reserved_slots}</Typography>
            </Stack>

          </Stack>
          <Stack flex="1 1 100%">
            <TableContainer component={Paper}>
              <Table size="small" padding="small">
                <TableHead>
                  <TableRow>
                    <StyledTableCell align="center" onClick={() => handleSort('activity_name', 'string')}>
                      <Stack justifyContent="center" alignItems="center" flexDirection="row">
                        Zajęcie
                        <SwapVertIcon />
                      </Stack>
                    </StyledTableCell>
                    <StyledTableCell align="center">Maksymalny stopień natężenia</StyledTableCell>
                    <StyledTableCell align="center" onClick={() => handleSort('current_load_rate', 'number')}>
                      <Stack justifyContent="center" alignItems="center" flexDirection="row">
                        Obecny stopień natężenia
                        <SwapVertIcon />
                      </Stack>
                    </StyledTableCell>
                    <StyledTableCell align="center" onClick={() => handleSort('remaining_load_rate', 'number')}>
                      <Stack justifyContent="center" alignItems="center" flexDirection="row">
                        Pozostały stopień natężenia
                        <SwapVertIcon />
                      </Stack>
                    </StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedData && sortedData.map((row, key) => (
                    <Row key={key} row={row} updateRowCallback={updateRowCallback} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </Box>
      </form>
    </>

  );
};

function Row({ row, updateRowCallback }) {
  const [maximumLoadRate, setMaximumLoadRate] = React.useState(row.maximum_load_rate);
  const [updateSignal, setUpdateSignal] = React.useState(false);

  useDidMountEffect(() => {
    updateRowCallback({ ...row, maximum_load_rate: maximumLoadRate });
  }, [updateSignal]);

  React.useEffect(() => {
    setMaximumLoadRate(row.maximum_load_rate);
  }, [row.maximum_load_rate]);

  return (
    <React.Fragment>
      <StyledTableRow>
        <TableCell align="center">
          <Typography>
            {row.activity_name}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <DebounceInput
            sx= {{ width: '90%', height: '50%' }}
            name="remarks"
            debounceTimeout={200}
            value={maximumLoadRate}
            element={SmallFormTextField}
            onChange={e => {
              setMaximumLoadRate(e.target.value);
              setUpdateSignal(!updateSignal);
            }}
            variant="outlined"
          />
        </TableCell>
        <TableCell align="center">
          {row.current_load_rate}
        </TableCell>
        <TableCell align="center">
          {row.remaining_load_rate}
        </TableCell>
      </StyledTableRow>
    </React.Fragment>
  );
}
